import { differenceBy, flattenDepth, isFunction, isString, isNumber } from 'lodash'
import FormError from './form-error'

/**
 * A combiner of validators. Just pass a series of functions and will combine their result
 * in one FormError (with the corresponding specified errors in the reasons).
 *
 * A validator is just a function that gets a value and return either null or an Error:
 *
 * @example
 * const isOverFive = (age) => age > 5 ? null : new Error('Should be over five')
 *
 * If a string|integer is passed in the arguments, it will be used as a fieldName|index
 * to apply the validators on in the model:
 *
 * @example
 * const checkUser = validate('age', isOverFive)
 * const error = checkUser({ age: 4 })
 *
 * The beautiful thing about validate, is that it can be hierarchically combined, since validate
 * itself is a function that gets a value (a model) and returns an Error or null (if no error occurred):
 *
 * @example
 * const boysOnly = (user) => user.gender === 'male' && user.age < 18 ? null : new Error('Boys only!')
 * const germanyOnly = (country) => country === 'DE' ? null : new Error('Germany only!')
 * const checkExtendedUser = validate(
 *      boysOnly,
 *      validate('age', isOverFive),
 *      validate('location', validate('country', germanyOnly))
 * )
 * const error = checkExtendedUser({ gender: 'male', age: '16', location: { country: 'IT' } })
 *
 * @param {function|string} args - the validation functions (plus, optionally, a fieldname)
 * @returns {function} the validator function to use on any model
 */
export function validate(...args) {
    // Filter all the validation functions
    const validators = args.filter(isFunction)

    // If at least one string is given, it will be a pecific model field name
    const rest = differenceBy(args, validators).filter(obj => isString(obj) || isNumber(obj))
    const fieldName = rest.length ? rest[0] : null

    return model => {
        // Model could be null a this stage, if validating a specific field, set null as model
        // and let the validators react on it
        const value = model && (fieldName || fieldName === 0) ? model[fieldName] : model
        const errors = flattenDepth(validators.map(validator => validator(value))).filter(response => response)
        return errors.length ? new FormError(errors[0].message, fieldName, errors) : null
    }
}

/**
 * A helper function for those who want to keep the code expressive. It allows the fieldName or index
 * to be defined separately from the validator functions. Apart of that, it does exactly the
 * same as the validator.
 *
 * @example
 * const checker = validate.for('name')(isCapitalized)
 * # Equals to:
 * const checkr2 = validate('name', isCapitalized)
 *
 * @param {string|number} fieldName
 * @returns {function} the validate function configured for the given field|index
 */
validate.for = function (fieldName) {
    return (...args) => {
        return validate(fieldName, ...args)
    }
}

/**
 * A helper-validator for array-models to apply the given validators to each item
 * in the array. It should be used as one of the validators within the validate function.
 *
 * @example
 * const noPink = (color) => color  === 'pink' ? new Error('No pink, please!') : null
 * const maxThree = (array) => array.length > 3 ? new Error('No more than three items!') : null
 *
 * const validateArray = validate(maxThree, validate.items(noPink))
 * const error = validateArray(['red', 'yellow', 'green', 'black', 'pink'])
 *
 * @param {function} args - the validation functions
 * @returns {function}
 */
validate.items = function (...args) {
    return array => (Array.isArray(array) ? array.map((item, index) => validate.for(index)(...args)(array)) : null)
}

/**
 * A helper-validator to check if at least one of the validators returns no error. This is useful
 * if only one of multiple fields is mandatory and can also be used for either-or-validation
 *
 * @example
 * const noPink = (color) => color  === 'pink' ? new Error('No pink, please!') : null
 * const maxThree = (array) => array.length > 3 ? new Error('No more than three items!') : null
 *
 * const validateArray = validate(validate.oneOf(noPink, maxThree))
 * console.log(validateArray(['red', 'yellow', 'green', 'black', 'pink'])) // No pink, please!
 * console.log(validateArray(['red', 'black', 'pink'])) // null
 * console.log(validateArray(['red', 'yellow', 'green', 'black'])) // null
 *
 * @param {function} args - the validation functions
 * @returns {function}
 */
validate.oneOf = function (...args) {
    return model => {
        if (args.some(validator => !validator(model))) {
            return null
        }
        return args[0](model)
    }
}
